import { ReactElement, useMemo, useState } from 'react'
import { Box, Stack } from '@mui/material'
import { TwoPaneElementDrawer } from '@editor/components/ElementListPane'
import usePlansQuery from '@editor/pages/Plans/hooks/usePlansQuery'
import { Tabs } from '@ui/navigation'
import { useSnackbar } from '@ui/snackbar'
import {
  useModelStore,
  useCommentFilterStore,
  useControlStore,
  useEditorViewStore,
} from '@editorStores'
import type { PlanViewTab } from '@editorStores'
import {
  useBlockAction,
  useDebouncedPlanSettingsSave,
  useEnterEditorPage,
  useFloorplanClickListener,
  useModelStoreys,
} from '@editorHooks'
import PlanViewCreateSection from './components/PlanViewCreateSection'
import PlanViewDisplaySection from './components/PlanViewDisplaySection'
import PlanViewElementTab from './components/PlanViewElementTab'
import PlanViewPlanElementDrawer from './components/PlanViewPlanElementDrawer'
import PlanViewPlanElementList from './components/PlanViewPlanElementList'
import PlanViewPlanSettingsSection from './components/PlanViewPlanSettingsSection'
import PlanViewPlansTab from './components/PlanViewPlansTab'
import PlanViewSettingsSection from './components/PlanViewSettingsSection'
import {
  PLAN_VIEW_PLAN_PDF_BLOCK_ACTION,
  PLAN_VIEW_PLAN_PDF_CHANGED_MESSAGE,
  PlanViewPlanChoice,
  PlanViewPlanType,
} from './constants'
import useHideNonExportedPositions from './hooks/useHideNonExportedPositions'
import useLoadStiffeningSegments from './hooks/useLoadStiffeningSegments'
import usePlanTemplateImage from './hooks/usePlanTemplateImage'
import usePlanViewEsc from './hooks/usePlanViewEsc'
import usePlanViewPreset from './hooks/usePlanViewPreset'
import usePlanViewSceneSession from './hooks/usePlanViewSceneSession'
import usePlanViewTab from './hooks/usePlanViewTab'
import useSelectPlanViewElement from './hooks/useSelectPlanViewElement'
import { getPlanViewStoreys, resolvePlanViewStoreys, toPlanViewPlanType } from './utils'

/**
 * "Planansicht": a floorplan preview of the exported plan. Choosing the storey(s) and a plan
 * (Positionsplan / Aussteifungsplan) drives the scene the way the export draws it — only the
 * exported element types (and, for the position plan, only the exported positions), the plan's
 * labels, real wall footprints, the plan area and the plan's comments, tables and images — so the
 * user arranges labels and plan elements on exactly what the PDF will show. "Vorlage anzeigen"
 * additionally draws the plan template (frame, notes, Plankopf) under the floorplan.
 */
const PlanView = (): ReactElement => {
  useEnterEditorPage('plan-view')

  const storeys = useModelStoreys()
  const floorplanStorey = useControlStore(state => state.floorplanState.storey)
  const visibleStoreys = useModelStore(state => state.visibleStoreys)
  // "Dach" is previewed on the top storey's floorplan with only the roof storey visible.
  const selectedStoreys = useMemo(
    () => getPlanViewStoreys(floorplanStorey, visibleStoreys),
    [floorplanStorey, visibleStoreys],
  )
  // What the scene previews (set by the plan preset); the chosen Plantyp may be the load plan,
  // which has no preview, in which case the scene keeps showing this under the notice.
  const selectedPlan = useCommentFilterStore(state => state.selectedPlan)
  const planType: PlanViewPlanType = toPlanViewPlanType(selectedPlan)
  const planChoice = useEditorViewStore(state => state.planView.planChoice)
  const setPlanChoice = useEditorViewStore(state => state.setPlanViewPlanChoice)
  const isLoadPlanSelected = planChoice === 'loadPlan'
  const [showTemplate, setShowTemplate] = useState(true)
  const { data: plans } = usePlansQuery()
  const { tab, setTab, hasSelectedElement, planElementDrawerType } = usePlanViewTab()
  const elementListCollapsed = useControlStore(state => state.elementListCollapsed)
  const { enqueueSnackbar } = useSnackbar()
  const { isBlockedFn: isPlanPdfChangedFn } = useBlockAction(
    PLAN_VIEW_PLAN_PDF_BLOCK_ACTION,
    'subscriber',
  )

  // Leaving "Pläne" unmounts the preview, which would drop unsaved PDF changes.
  const onTabChange = (nextTab: PlanViewTab) => {
    if (nextTab !== tab && isPlanPdfChangedFn()) {
      enqueueSnackbar(PLAN_VIEW_PLAN_PDF_CHANGED_MESSAGE, { variant: 'warning' })
      return
    }
    setTab(nextTab)
  }

  const { onPlanSettingsChange, flushPlanSettings, stateIndicatorType } =
    useDebouncedPlanSettingsSave()
  // Changed plan settings not saved yet (debounce pending or request in flight).
  const isPlanSettingsSavePending =
    stateIndicatorType === 'unsaved' || stateIndicatorType === 'loading'

  // Clicking an element's geometry (or its label, see `FloorplanModelLabels`) opens "Bauteil".
  const selectPlanViewElement = useSelectPlanViewElement()
  useFloorplanClickListener(selectPlanViewElement)

  usePlanViewEsc()

  const applyPreset = usePlanViewPreset()
  usePlanViewSceneSession(applyPreset)
  useHideNonExportedPositions(planType === 'positionPlan')
  useLoadStiffeningSegments(planType === 'stiffeningPlan')
  const { isLoadingTemplateImage } = usePlanTemplateImage(showTemplate, planType)

  const onStoreysChange = (nextStoreys: string[]) => {
    const resolved = resolvePlanViewStoreys(nextStoreys, storeys, planType)
    if (resolved.length) applyPreset(planType, resolved)
  }

  const onPlanChoiceChange = (nextPlanChoice: PlanViewPlanChoice) => {
    setPlanChoice(nextPlanChoice)
    // The load plan has no preview: the scene keeps the last previewed plan under the notice.
    if (nextPlanChoice === 'loadPlan') return
    const resolved = resolvePlanViewStoreys(selectedStoreys, storeys, nextPlanChoice)
    if (resolved.length) applyPreset(nextPlanChoice, resolved)
  }

  return (
    <TwoPaneElementDrawer
      collapsed={elementListCollapsed}
      // "Pläne" covers the scene, so nothing can be selected there.
      list={tab === 'plans' && !planElementDrawerType ? null : <PlanViewPlanElementList />}
    >
      {/* A selected comment / table / image card takes over the detail column (tabs included): its
          form replaces the plan view until it is closed or deselected. */}
      {planElementDrawerType && (
        <PlanViewPlanElementDrawer planElementType={planElementDrawerType} />
      )}
      {/* Hidden rather than unmounted meanwhile, so a running plan generation and the plan
          creation form keep their state. */}
      <Stack
        sx={{ height: '100%', minHeight: 0, display: planElementDrawerType ? 'none' : 'flex' }}
      >
        <Box sx={{ flexShrink: 0 }}>
          <Tabs
            items={[
              { id: 'preview', label: 'Planansicht' },
              ...(hasSelectedElement ? [{ id: 'element', label: 'Bauteil' }] : []),
              { id: 'plans', label: `Pläne (${plans?.length ?? 0})` },
            ]}
            value={tab}
            onChange={id => onTabChange(id as PlanViewTab)}
            size="md"
            kind="folder"
          />
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {tab === 'plans' ? (
            <PlanViewPlansTab />
          ) : tab === 'element' ? (
            <PlanViewElementTab />
          ) : (
            <Box sx={{ px: 2, py: 1 }}>
              <PlanViewSettingsSection
                storeys={storeys}
                selectedStoreys={selectedStoreys}
                planType={planType}
                planChoice={planChoice}
                onStoreysChange={onStoreysChange}
                onPlanChoiceChange={onPlanChoiceChange}
              />
              <Box sx={{ pt: 1 }}>
                <PlanViewPlanSettingsSection
                  onPlanSettingsChange={onPlanSettingsChange}
                  flushPlanSettings={flushPlanSettings}
                  stateIndicatorType={stateIndicatorType}
                />
              </Box>
              {/* The template is drawn under the scene preview, which the load plan doesn't have. */}
              {!isLoadPlanSelected && (
                <Box sx={{ pt: 1 }}>
                  <PlanViewDisplaySection
                    showTemplate={showTemplate}
                    onShowTemplateChange={setShowTemplate}
                    isLoadingTemplate={isLoadingTemplateImage}
                  />
                </Box>
              )}
              <Box sx={{ pt: 1 }}>
                <PlanViewCreateSection
                  storeys={selectedStoreys}
                  planChoice={planChoice}
                  isPlanSettingsSavePending={isPlanSettingsSavePending}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Stack>
    </TwoPaneElementDrawer>
  )
}

export default PlanView
